import React, { useState, useEffect } from 'react';
import { Search, Phone } from 'lucide-react';
import clsx from 'clsx';
import type { Conversation } from '../../stores/whatsapp/chatStore';
import ConversationItem from './ConversationItem';
import { useAuth } from '../../providers/auth';
import { useDeptVisibilityStore } from '../../stores/whatsapp/deptVisibilityStore';
import waApi from '../../services/whatsapp/waApi';

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
}

const MAIN_TABS = [
  { key: 'mine', label: 'Meus', showCount: true },
  { key: 'unassigned', label: 'Fila', showCount: true },
  { key: 'all', label: 'Todos', showCount: false },
] as const;

const PRIORITY_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'urgent', label: 'Urgente' },
  { key: 'high', label: 'Alta' },
  { key: 'medium', label: 'Media' },
  { key: 'low', label: 'Baixa' },
] as const;

type TabKey = (typeof MAIN_TABS)[number]['key'];

export default function ConversationList({
  conversations,
  activeId,
  onSelect,
}: ConversationListProps) {
  const { user } = useAuth();
  const currentUserId = user?.id?.toString();
  const currentUserName = user?.nome || '';
  // Departamento real do usuario (vem do campo departamento vinculado ao usuario)
  const currentUserDept = user?.departamento?.nome || '';
  // Visibilidade por departamento via store (ja considera depto principal + extras/allowed)
  // Reusamos o mesmo canSeeDept que ConversationItem/ChatWindow usam, para manter consistencia.
  const canSeeDept = useDeptVisibilityStore(s => s.canSeeDept);
  const deptRestricted = useDeptVisibilityStore(s => s.restrictByDept);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('mine');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [waNumberFilter, setWaNumberFilter] = useState('all');
  const [waNumbers, setWaNumbers] = useState<{ id: string; label: string; phone_number: string }[]>(
    [],
  );

  useEffect(() => {
    waApi
      .get('/wa-numbers/available')
      .then(res => {
        const nums = res.data || [];
        setWaNumbers(
          nums.map((n: any) => ({ id: n.id, label: n.label, phone_number: n.phone_number })),
        );
      })
      .catch(() => {});
  }, []);

  // Verifica se o usuario pode ver a conversa pelo departamento.
  // Usa o canSeeDept do store, que ja considera o depto PRINCIPAL + os extras/allowed
  // liberados ao grupo (antes esta funcao olhava so o depto principal e escondia conversas
  // de deptos extras que o usuario tinha permissao de ver — gap da task #28).
  const isMyDepartment = (c: Conversation): boolean => {
    // Sem restricao ativa (ex: admin): ve tudo
    if (!deptRestricted) return true;
    const convDept = c.assigned_department || c.department;
    if (!convDept) return true;
    // canSeeDept ja cobre principal + extras; retorna true quando autorizado
    if (canSeeDept(convDept)) return true;
    // Fallback local pelo depto principal (mesma logica de ConversationItem/ChatWindow),
    // mantem o comportamento de quem tem 1 depto caso o store nao esteja configurado.
    if (!currentUserDept) return true;
    const normalize = (s: string) => s.toLowerCase().trim();
    const userWords = normalize(currentUserDept)
      .split(/[\s.,-]+/)
      .filter(Boolean);
    const convWords = normalize(convDept)
      .split(/[\s.,-]+/)
      .filter(Boolean);
    const u = normalize(currentUserDept);
    const d = normalize(convDept);
    return u === d || u.includes(d) || d.includes(u) || userWords[0] === convWords[0];
  };

  const matchesWaNumber = (c: Conversation): boolean => {
    if (waNumberFilter === 'all') return true;
    return c.wa_number_id === waNumberFilter;
  };

  const isMyConversation = (c: Conversation): boolean => {
    if (!c.assigned_agent_id) return false;
    if (String(c.assigned_agent_id) === String(currentUserId)) return true;
    if (currentUserName && c.assigned_agent_name?.toLowerCase() === currentUserName.toLowerCase())
      return true;
    return false;
  };

  // Contagens (respeitam filtro de numero)
  const tabCounts: Record<string, number> = {
    mine: conversations.filter(
      c => isMyConversation(c) && c.status !== 'closed' && matchesWaNumber(c),
    ).length,
    unassigned: conversations.filter(
      c => !c.assigned_agent_id && c.status !== 'closed' && isMyDepartment(c) && matchesWaNumber(c),
    ).length,
  };

  // Filtragem de conversas
  const filtered = conversations
    .filter(c => {
      if (search) {
        const q = search.toLowerCase();
        const match =
          c.client_name?.toLowerCase().includes(q) ||
          c.client_phone?.includes(q) ||
          c.client_company?.toLowerCase().includes(q) ||
          c.subject?.toLowerCase().includes(q) ||
          c.assigned_agent_name?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (priorityFilter !== 'all' && c.priority !== priorityFilter) return false;
      if (!matchesWaNumber(c)) return false;
      switch (activeTab) {
        case 'mine':
          return isMyConversation(c) && c.status !== 'closed';
        case 'unassigned':
          return !c.assigned_agent_id && c.status !== 'closed' && isMyDepartment(c);
        default:
          // "Todos" — mostra tudo (blur e aplicado no ConversationItem e ChatWindow)
          return true;
      }
    })
    .sort((a, b) => {
      const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return dateB - dateA;
    });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="space-y-3 px-4 pb-2 pt-3">
        <h2 className="text-lg font-semibold tracking-tight text-gray-800">Conversas</h2>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar conversa..."
            className="w-full rounded-full border-none bg-gray-100/80 py-2 pl-9 pr-4 text-sm outline-none transition-all placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-green-500/30"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-1">
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
          {MAIN_TABS.map(tab => {
            const count = tab.showCount ? (tabCounts[tab.key] ?? 0) : 0;
            return (
              <button
                type="button"
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                  activeTab === tab.key
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                )}
              >
                {tab.label}
                {tab.showCount && count > 0 && (
                  <span
                    className={clsx(
                      'min-w-[18px] rounded-full px-1 py-0.5 text-center text-[10px] font-bold leading-none',
                      activeTab === tab.key ? 'bg-white/25 text-white' : 'bg-green-600 text-white',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority + Number filters */}
      <div className="space-y-1.5 px-4 pb-2">
        <div className="no-scrollbar flex gap-1 overflow-x-auto">
          {PRIORITY_FILTERS.map(pf => (
            <button
              type="button"
              key={pf.key}
              onClick={() => setPriorityFilter(pf.key)}
              className={clsx(
                'flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-medium transition-all',
                priorityFilter === pf.key
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-50 text-gray-400 hover:bg-gray-100',
              )}
            >
              {pf.label}
            </button>
          ))}
        </div>

        {waNumbers.length > 1 && (
          <div className="no-scrollbar flex items-center gap-1 overflow-x-auto">
            <Phone size={12} className="flex-shrink-0 text-gray-400" />
            <button
              type="button"
              onClick={() => setWaNumberFilter('all')}
              className={clsx(
                'flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-medium transition-all',
                waNumberFilter === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-50 text-gray-400 hover:bg-gray-100',
              )}
            >
              Todos
            </button>
            {waNumbers.map(n => (
              <button
                type="button"
                key={n.id}
                onClick={() => setWaNumberFilter(n.id)}
                className={clsx(
                  'flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-medium transition-all',
                  waNumberFilter === n.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100',
                )}
              >
                {n.label || n.phone_number}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <p className="text-sm">Nenhuma conversa encontrada</p>
          </div>
        ) : (
          filtered.map(conversation => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isActive={conversation.id === activeId}
              onClick={() => onSelect(conversation.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
